import React, { useRef, useState } from "react";
import styled from "styled-components";
import DataTable from "../components/DataTable";
import Components from "../components";
import { Helmet } from "react-helmet-async";
import PageTitle from "../components/PageTitle";
import { Button, Stack } from "@mui/material";
import FileSaver from "file-saver";
import apiClient from "../utils/apiClient";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import LinearProgressWithLabel from "../components/LinearProgressWithLabel";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const { Popup } = Components;

const HeadTabMenu = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
  gap: 10px;
`;

const VideoPlayerPopup = ({ videoUrl }) => {
  const { t } = useTranslation();

  return (
    <video
      controls
      autoPlay
      style={{ width: "100%", maxHeight: "70vh", objectFit: "contain" }}
    >
      <source src={videoUrl} type="video/mp4" />
      {t("training.videoNotSupported")}
    </video>
  );
};

const Training = () => {
  const { t } = useTranslation();

  const [refreshCount, setRefreshCount] = useState(0);
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState();
  const roles = useSelector((state) => state.user.roles);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleUpdate = () => {
    setRefreshCount((prev) => prev + 1);
  };

  const openVideoPopup = (item) => {
    setSelectedFile(item);
    const videoUrl = `/api/training-files/stream/${item.filename}`;
    setCurrentVideoUrl(videoUrl);
    setShowVideoPopup(true);
  };

  const downloadDocument = async (document) => {
    try {
      const response = await apiClient.get(
        `/training-files/download/${document.filename}`,
        {
          responseType: "blob",
        },
      );
      const blob = new Blob([response.data]);
      FileSaver.saveAs(blob, document.filename);
    } catch (error) {
      console.error(t("training.downloadErrorLog"), error);
    }
  };

  const deleteDocument = async (document) => {
    try {
      const confirm = window.confirm(t("training.confirmDeleteFile"));
      if (!confirm) return;

      await apiClient.delete(`/training-files/${document.filename}`);
      handleUpdate();
    } catch (error) {
      console.error(t("training.deleteErrorLog"), error);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadProgress(0);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await apiClient.post("/training-files", formData, {
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress(percent);
        },
      });
      alert(t("training.fileUploaded"));
      setRefreshCount((prev) => prev + 1);
    } catch (err) {
      console.error(t("training.uploadErrorLog"), err);
      alert(t("training.uploadError"));
    } finally {
      e.target.value = null;
      setUploadProgress(0);
    }
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const columns = [
    {
      field: "format",
      headerName: t("training.columns.format"),
      flex: 1,
      valueGetter: (value, row) => {
        const formatType = String(row.format_type || "").toLowerCase();

        return t(`training.format.${formatType}`, {
          defaultValue: row.format || t("training.format.document"),
        });
      },
    },
    {
      field: "date",
      headerName: t("training.columns.date"),
      flex: 1,
      valueFormatter: (value) => {
        return value ? dayjs(value).format("DD.MM.YYYY") : "";
      },
    },
    {
      field: "title",
      headerName: t("training.columns.title"),
      flex: 2,
    },
    {
      field: "action",
      headerName: "",
      flex: 1,
      sortable: false,
      type: "actions",
      renderCell: (params) => {
        const actions = [];

        if (params.row.format_type === "video") {
          actions.push(
            <Button
              key={"openVideoPopup"}
              size="small"
              variant="contained"
              onClick={() => openVideoPopup(params.row)}
            >
              {t("training.watch")}
            </Button>,
          );
        } else {
          actions.push(
            <Button
              key={"downloadDocument"}
              size="small"
              variant="contained"
              onClick={() => downloadDocument(params.row)}
            >
              {t("common.download")}
            </Button>,
          );
        }

        if (roles?.includes("AGENTADMIN")) {
          actions.push(
            <Button
              key={"deleteDocument"}
              size="small"
              variant="outlined"
              color="error"
              onClick={() => deleteDocument(params.row)}
            >
              {t("common.delete")}
            </Button>,
          );
        }

        return (
          <Stack direction="row" spacing={1}>
            {actions}
          </Stack>
        );
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("training.metaTitle")}</title>
        <meta name="description" content={t("training.metaDescription")} />
      </Helmet>

      <PageTitle>{t("training.title")}</PageTitle>

      <HeadTabMenu>
        {roles?.includes("AGENTADMIN") && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleChooseFile}
            fullWidth
            startIcon={<AddIcon />}
          >
            {t("common.addFile")}
          </Button>
        )}

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleUpdate}
          fullWidth
        >
          {t("common.refresh")}
        </Button>

        <input
          style={{ display: "none" }}
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </HeadTabMenu>

      {uploadProgress > 0 && (
        <Box sx={{ width: "50%", mb: 2 }}>
          <LinearProgressWithLabel value={uploadProgress} color="primary" />
        </Box>
      )}

      <DataTable
        columns={columns}
        path={"/training-files"}
        refresh={refreshCount}
      />

      <Popup
        show={showVideoPopup}
        title={selectedFile?.title}
        onClose={() => setShowVideoPopup(false)}
        ContentComponent={VideoPlayerPopup}
        contentProps={{
          videoUrl: currentVideoUrl,
          onClose: () => setShowVideoPopup(false),
        }}
      />
    </>
  );
};

export default Training;
